import React, { useRef } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";
import { Box, Select, MenuItem, Typography } from "@mui/material";

import { AddCircleOutline as AddCircleOutlineIcon } from "@mui/icons-material";
import CachedIcon from "@mui/icons-material/Cached";

import useAuth from "../../../../hooks/useAuth";
import useRule from "../../../../hooks/super/useRule";
import { CollapseAlert, IOSSwitch } from "../../../../components/pages/common/styled";
import CustomRuleTable from "../component/T_CustomRule";
import { CrsRuleNo, UserType } from "../../../../utils/constants";

import PageHeader from "../../../../components/pages/_new/super/common/PageHeader";
import PrimaryButton from "../../../../components/pages/_new/super/common/PrimaryButton";
import OutlinedButton from "../../../../components/pages/_new/super/common/OutlinedButton";
import {
  TOKENS,
  SectionCard,
} from "../../../../components/pages/_new/super/design";

function SACustomRule() {
  const navigate = useNavigate();
  const { isAuthenticated, adminUserType } = useAuth();
  const { getCustomRules, curruleEnableChange, selectCurrentRule, errMsg, setErr, currule } =
    useRule();
  const [errOpen, setErrOpen] = React.useState(false);

  const ruleID = useRef(CrsRuleNo.CUSTOM);
  const [currule_enable, set_currule_enable] = React.useState();

  React.useEffect(() => {
    if (isAuthenticated) {
      selectCurrentRule(ruleID.current.toString());
      getCustomRules();
    }
  }, [isAuthenticated, getCustomRules, selectCurrentRule, ruleID]);

  React.useEffect(() => {
    if (currule === null || currule === undefined) return;
    set_currule_enable(currule?.enabled);
  }, [currule]);

  const crsRuleChange = () => {
    curruleEnableChange({
      rule_id: currule.rule_id,
      enable: !currule_enable,
    });
    set_currule_enable(!currule_enable);
  };

  React.useEffect(() => {
    setErrOpen(true);
  }, [errMsg]);

  const gotoAddNewCustomRule = () => {
    navigate("/super/application/rule/custom/new");
  };
  const refresh = () => getCustomRules();

  const canEdit = [UserType.SUPER_ADMIN, UserType.ADMIN].includes(adminUserType);

  return (
    <React.Fragment>
      <Helmet title="Custom Rule" />
      <PageHeader
        title="Custom Rule Management"
        description="Manage tenant-spanning Custom WAF Rules"
        actions={
          <>
            <OutlinedButton
              onClick={refresh}
              startIcon={<CachedIcon style={{ width: 18, height: 18 }} />}
            >
              Refresh
            </OutlinedButton>
            <PrimaryButton
              onClick={gotoAddNewCustomRule}
              startIcon={<AddCircleOutlineIcon style={{ width: 18, height: 18 }} />}
              disabled={!canEdit}
            >
              Add Custom Rule
            </PrimaryButton>
          </>
        }
      />
      <Box sx={{ mb: 3 }}>
        <CollapseAlert errOpen={errOpen} setErrOpen={setErrOpen} setErr={setErr} errMsg={errMsg} />
      </Box>

      <Box sx={{ display: "grid", gap: 2 }}>
        <SectionCard title="Category">
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
            <Select
              value={ruleID.current}
              size="small"
              sx={{
                minWidth: 360,
                borderRadius: `${TOKENS.radiusXs}px`,
                background: TOKENS.bgSurface,
              }}
            >
              <MenuItem value={CrsRuleNo.CUSTOM}>Custom Rules</MenuItem>
            </Select>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                border: `1px solid ${TOKENS.border}`,
                borderRadius: `${TOKENS.radiusXs}px`,
                px: 1.5,
                py: "6px",
                background: TOKENS.bgSurface,
              }}
            >
              <Typography sx={{ fontSize: 13, color: TOKENS.fg1 }}>Category enabled</Typography>
              <IOSSwitch
                checked={!!currule_enable}
                onChange={crsRuleChange}
                disabled={!canEdit}
              />
            </Box>
          </Box>
        </SectionCard>

        <SectionCard title="Custom Rules">
          <CustomRuleTable disabled={!currule_enable} />
        </SectionCard>
      </Box>
    </React.Fragment>
  );
}

export default SACustomRule;
